import { ReactNode, useState, createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from '../Icon.js';
import { WithTooltip } from './Tooltip.js';

// ── Confirm context ───────────────────────────────────────────────────────────

type SABConfirmState = { label: string; onConfirm: () => void } | null;
type SABConfirmCtxType = { request: (label: string, cb: () => void) => void } | null;
const SABCtx = createContext<SABConfirmCtxType>(null);

// ── Root ──────────────────────────────────────────────────────────────────────

interface SelectionActionBarProps {
  visible: boolean;
  countLabel: string;
  onClearSelection: () => void;
  children?: ReactNode;
}

function SelectionActionBarRoot({
  visible,
  countLabel,
  onClearSelection,
  children,
}: SelectionActionBarProps) {
  const { t } = useTranslation();
  const [pendingConfirm, setPendingConfirm] = useState<SABConfirmState>(null);

  return (
    <SABCtx.Provider value={{ request: (label, cb) => setPendingConfirm({ label, onConfirm: cb }) }}>
      <div
        style={{
          overflow: 'hidden',
          maxHeight: visible ? 'var(--evo-toolbar-height)' : '0',
          opacity: visible ? 1 : 0,
          transition: 'max-height 150ms ease-out, opacity 150ms ease-out',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            height: 'var(--evo-toolbar-height)',
            padding: '0 var(--evo-content-pad-x)',
            background: 'var(--evo-selectbar-bg)',
            borderTop: '1px solid var(--evo-stroke)',
            borderBottom: '1px solid var(--evo-cyan)',
          }}
        >
          {/* Count label */}
          <span
            style={{
              fontFamily: 'var(--evo-font)',
              fontSize: 'var(--evo-fs-body)',
              fontWeight: 600,
              color: 'var(--evo-cyan)',
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >
            {countLabel}
          </span>

          {/* Divider */}
          <div
            style={{
              width: '1px',
              height: 'var(--evo-divider-h)',
              background: 'var(--evo-stroke)',
              flexShrink: 0,
              margin: '0 8px',
            }}
          />

          {/* Actions or confirm overlay */}
          {pendingConfirm ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
              <span style={{
                fontFamily: 'var(--evo-font)',
                fontSize: '12px',
                color: 'var(--evo-danger)',
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}>
                {t('common.areYouSure')}
              </span>
              <SABBtn label={t('common.cancel')} tone="default" onClick={() => setPendingConfirm(null)} />
              <SABBtn
                label={pendingConfirm.label}
                tone="danger-fill"
                onClick={() => { pendingConfirm.onConfirm(); setPendingConfirm(null); }}
              />
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, flexWrap: 'wrap', minWidth: 0 }}>
              {children}
            </div>
          )}

          {/* Clear (×) */}
          <WithTooltip label={t('common.close')}>
          <button
            type="button"
            title={t('common.close')}
            onClick={() => { setPendingConfirm(null); onClearSelection(); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 'var(--evo-icon-btn-sm)',
              height: 'var(--evo-icon-btn-sm)',
              borderRadius: 'var(--evo-r-sm)',
              border: '1px solid var(--evo-stroke)',
              background: 'var(--evo-surface-lift)',
              color: 'var(--evo-fg-3)',
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'opacity 120ms',
            }}
          >
            <Icon name="close" size={12} />
          </button>
          </WithTooltip>
        </div>
      </div>
    </SABCtx.Provider>
  );
}

// ── Palette ───────────────────────────────────────────────────────────────────

type SABTone = 'default' | 'primary' | 'danger' | 'danger-fill' | 'amber';

const SAB_PALETTE: Record<SABTone, { border: string; color: string; bg: string }> = {
  'default':     { border: 'var(--evo-stroke)',  color: 'var(--evo-fg-1)',   bg: 'var(--evo-surface-lift)' },
  'primary':     { border: 'var(--evo-cyan)',     color: 'var(--evo-fg-on-cyan)', bg: 'var(--evo-cyan)'    },
  'danger':      { border: 'color-mix(in srgb, var(--evo-danger) 40%, transparent)', color: 'var(--evo-danger)', bg: 'transparent' },
  'danger-fill': { border: 'var(--evo-danger)',   color: 'var(--evo-fg-1)',   bg: 'var(--evo-danger)'  },
  'amber':       { border: 'var(--evo-amber)',    color: 'var(--evo-amber)',  bg: 'transparent'        },
};

// ── Internal button (no context, used by confirm overlay) ─────────────────────

interface SABBtnProps {
  label: string;
  tone?: SABTone;
  onClick?: () => void;
  loading?: boolean;
  disabled?: boolean;
}

function SABBtn({ label, tone = 'default', onClick, loading, disabled }: SABBtnProps) {
  const c = SAB_PALETTE[tone];
  const isDisabled = disabled || loading;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isDisabled}
      style={{
        height: 'var(--evo-control-h)',
        padding: '0 12px',
        borderRadius: 'var(--evo-r-sm)',
        border: `1px solid ${c.border}`,
        background: c.bg,
        color: c.color,
        fontFamily: 'var(--evo-font)',
        fontSize: 'var(--evo-fs-label)',
        fontWeight: 500,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.5 : 1,
        transition: 'opacity 120ms',
        flexShrink: 0,
        whiteSpace: 'nowrap',
      }}
    >
      {loading ? '…' : label}
    </button>
  );
}

// ── Public Action (context-aware, supports confirmRequired) ───────────────────

interface SABActionProps extends SABBtnProps {
  confirmRequired?: boolean;
}

function SABAction({ confirmRequired, onClick, ...rest }: SABActionProps) {
  const ctx = useContext(SABCtx);

  function handleClick() {
    if (confirmRequired && ctx) {
      ctx.request(rest.label, () => onClick?.());
    } else {
      onClick?.();
    }
  }

  return <SABBtn {...rest} onClick={handleClick} />;
}

// ── IconAction — acción de solo icono en la barra (spec V2) ──────────────────
// bell = fill-warning/amber · default = surface-lift/fg-1 · danger =
// fill-danger/danger. Cuadrado de --evo-control-h, icono 15, tooltip
// obligatorio con el mismo texto que el title/aria.

type SABIconTone = 'default' | 'amber' | 'danger';

const SAB_ICON_PALETTE: Record<SABIconTone, { bg: string; color: string; border: string }> = {
  default: { bg: 'var(--evo-surface-lift)', color: 'var(--evo-fg-1)',    border: 'var(--evo-fg-1)'    },
  amber:   { bg: 'var(--evo-fill-warning)', color: 'var(--evo-amber)',   border: 'var(--evo-amber)'   },
  danger:  { bg: 'var(--evo-fill-danger)',  color: 'var(--evo-danger)',  border: 'var(--evo-danger)'  },
};

interface SABIconActionProps {
  icon: IconName;
  label: string;
  tone?: SABIconTone;
  onClick?: () => void;
  loading?: boolean;
  disabled?: boolean;
  confirmRequired?: boolean;
}

function SABIconAction({ icon, label, tone = 'default', onClick, loading, disabled, confirmRequired }: SABIconActionProps) {
  const ctx = useContext(SABCtx);
  const c = SAB_ICON_PALETTE[tone];
  const isDisabled = (disabled ?? false) || (loading ?? false);

  function handleClick() {
    if (isDisabled) return;
    if (confirmRequired && ctx) {
      ctx.request(label, () => onClick?.());
    } else {
      onClick?.();
    }
  }

  return (
    <WithTooltip label={label}>
      <button
        type="button"
        title={label}
        aria-label={label}
        disabled={isDisabled}
        onClick={handleClick}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 'var(--evo-control-h)',
          height: 'var(--evo-control-h)',
          padding: 0,
          borderRadius: 'var(--evo-r)',
          background: c.bg,
          color: c.color,
          border: `var(--evo-bw-tag) solid ${c.border}`,
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          opacity: isDisabled ? 0.5 : 1,
          boxSizing: 'border-box',
          flexShrink: 0,
          transition: 'opacity 120ms',
        }}
      >
        <Icon name={icon} size={15} />
      </button>
    </WithTooltip>
  );
}

// ── Divider ───────────────────────────────────────────────────────────────────

function Divider() {
  return (
    <div
      style={{
        width: 1,
        height: 'var(--evo-divider-h)',
        background: 'var(--evo-stroke)',
        margin: '0 8px',
        flexShrink: 0,
      }}
    />
  );
}

// ── Export ────────────────────────────────────────────────────────────────────

export const SelectionActionBar = Object.assign(SelectionActionBarRoot, { Action: SABAction, IconAction: SABIconAction, Divider });
